import {detect} from 'detect-browser'

import type {AllActions, AsyncAction,
  DisplayToastMessageAction, PageIsLoadedAction, ProjectAction, RootState,
  VisualElementAction} from './actions'
import isMobileVersion from './mobile'


export const daysSince = (timestamp: number | string): number => {
  const day = new Date(timestamp)
  day.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return Math.round((today.getTime() - day.getTime()) / 86_400_000)
}


const getUser = (action: AllActions, state: RootState): bayes.bob.User => {
  if (state.user && state.user.userId) {
    return state.user
  }
  return state.user || {}
}

export interface Properties {
  [feature: string]: string | readonly string[] | boolean | number
}

const flattenFeatureFlags = (featuresEnabled: bayes.bob.Features): Properties => {
  const features: Properties = {}
  const boolFeatures: string[] = []
  for (const feature in featuresEnabled) {
    const featureValue = featuresEnabled[feature as keyof bayes.bob.Features]
    if (featureValue === true) {
      boolFeatures.push(feature)
    } else if (featureValue) {
      features['Features.' + feature] = featureValue
    }
  }
  features['Features'] = boolFeatures.sort()
  return features
}


export class Logger {
  private actionTypesToLog: {[actionType: string]: string}

  private browser: {name?: string} = detect() || {}

  public constructor(actionTypesToLog: {[actionType: string]: string}) {
    this.actionTypesToLog = actionTypesToLog
  }

  public shouldLogAction(action: AllActions): boolean {
    if (!this.actionTypesToLog[action.type]) {
      return false
    }
    const asyncAction = action as AsyncAction<string, void>
    if (asyncAction.ASYNC_MARKER && !asyncAction.status) {
      // For async action, log only when the action returns ie when status is here.
      return false
    }
    return true
  }

  public getEventName(action: AllActions): string {
    return this.actionTypesToLog[action.type]
  }

  public getEventProperties(action: AllActions, state: RootState): Properties {
    const properties: Properties = {}
    if (this.browser.name) {
      properties['$browser'] = this.browser.name
    }
    if (isMobileVersion) {
      properties['Mobile Version'] = true
    }
    if (action.type === 'PAGE_IS_LOADED') {
      const path = action.location.pathname.slice(1)
      properties['path'] = path
    }
    const user = getUser(action, state)
    if (user.registeredAt) {
      properties['Days since Registration'] = daysSince(user.registeredAt)
    }
    if (user.userId && !user.hasAccount) {
      properties['Is Guest'] = true
    }
    if (user.featuresEnabled) {
      Object.assign(properties, flattenFeatureFlags(user.featuresEnabled))
    }
    const projectAction = action as ProjectAction<string>
    if (projectAction.project) {
      const {city, diagnostic, originalSelfDiagnostic, targetJob} = projectAction.project
      const {masculineName = ''} = targetJob || {}
      if (masculineName) {
        properties['Project Job Name'] = masculineName
      }
      const {name: cityName = ''} = city || {}
      if (cityName) {
        properties['Project City'] = cityName
      }
      const {categoryId = ''} = diagnostic || {}
      if (categoryId) {
        properties['Bob Thinks'] = categoryId
      }
      if (categoryId && originalSelfDiagnostic?.categoryId) {
        properties["Bob Thinks matches user's self diagnostic"] =
          categoryId === originalSelfDiagnostic?.categoryId
      }
    }
    const errorAction = action as DisplayToastMessageAction
    if (errorAction.error) {
      properties['Error'] = errorAction.error
    }
    const asyncAction = action as {readonly status: 'error' | 'sending' | 'success'}
    if (asyncAction.status) {
      properties['Async response'] = asyncAction.status
    }
    const visualElementAction = action as VisualElementAction<string>
    if (visualElementAction.visualElement) {
      properties['Visual Element Source'] = visualElementAction.visualElement
    }
    const pageIsLoadedAction = action as PageIsLoadedAction
    if (pageIsLoadedAction.timeToFirstInteractiveMillisecs) {
      properties['Page loading time (ms)'] = pageIsLoadedAction.timeToFirstInteractiveMillisecs
    }
    return properties
  }

  public getUserId(action: AllActions, state: RootState): string|undefined {
    return getUser(action, state).userId
  }

  public getUserProperties(action: AllActions, state: RootState): Properties|null {
    const {featuresEnabled, profile} = getUser(action, state)

    const {
      email = '',
      gender = '',
      highestDegree = '',
      situation = '',
      yearOfBirth = 0,
    } = profile || {}
    const profileData: Properties = {
      deployment: config.name,
    }
    profileData['$hostname'] = window.location.hostname
    if (gender) {
      profileData['Gender'] = gender
    }
    if (highestDegree) {
      profileData['Highest Degree'] = highestDegree
    }
    if (situation) {
      profileData['Situation'] = situation
    }
    if (yearOfBirth) {
      profileData['Year Of Birth'] = yearOfBirth
    }
    if (/@(bayes.org|example.com|bayesimpact.org)$/.test(email)) {
      profileData['Is Test User?'] = true
    }
    if (featuresEnabled) {
      Object.assign(profileData, flattenFeatureFlags(featuresEnabled))
    }
    if (!Object.keys(profileData)) {
      return null
    }
    return profileData
  }
}
